import type { LocaleCode } from '../src/shared/contracts';

const PARENT_PIN = '6661313';
const REQUIRED_TAPS = 5;
const TAP_WINDOW_MS = 2_000;

interface ParentModeCopy {
  cancel: string;
  close: string;
  confirmReset: string;
  incorrectPin: string;
  locked: (seconds: number) => string;
  parentMode: string;
  pinHelp: string;
  pinLabel: string;
  remaining: string;
  reset: string;
  resetComplete: string;
  resetPrompt: (watched: string) => string;
  resetTitle: string;
  unlock: string;
}

const COPY: Record<LocaleCode, ParentModeCopy> = {
  en: {
    cancel: 'Cancel',
    close: 'Close',
    confirmReset: 'Reset timer',
    incorrectPin: 'Incorrect PIN.',
    locked: (seconds) => `Too many attempts. Try again in ${seconds} seconds.`,
    parentMode: 'Parent mode',
    pinHelp: 'Enter the parent PIN.',
    pinLabel: 'PIN',
    remaining: 'Video time remaining today',
    reset: "Reset today's timer",
    resetComplete: "Today's video timer has been reset.",
    resetPrompt: (watched) => `Reset ${watched} of watched video time?`,
    resetTitle: 'Reset video timer?',
    unlock: 'Unlock',
  },
  ru: {
    cancel: 'Отмена',
    close: 'Закрыть',
    confirmReset: 'Сбросить таймер',
    incorrectPin: 'Неверный PIN-код.',
    locked: (seconds) => `Слишком много попыток. Попробуйте через ${seconds} сек.`,
    parentMode: 'Режим для родителей',
    pinHelp: 'Введите PIN-код для родителей.',
    pinLabel: 'PIN-код',
    remaining: 'Оставшееся время видео сегодня',
    reset: 'Сбросить таймер на сегодня',
    resetComplete: 'Таймер видео на сегодня сброшен.',
    resetPrompt: (watched) => `Сбросить просмотренное время (${watched})?`,
    resetTitle: 'Сбросить таймер видео?',
    unlock: 'Открыть',
  },
};

interface ParentModeOptions {
  trigger: HTMLElement;
  modal: HTMLElement;
  getLocale: () => LocaleCode;
  getTimeLeft: () => number;
  getWatchedSeconds: () => number;
  resetWatchBudget: () => void;
}

function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  const remainder = seconds % 60;
  const parts: string[] = [];
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (remainder || parts.length === 0) parts.push(`${remainder}s`);
  return parts.join(' ');
}

export class ParentModeController {
  private tapTimes: number[] = [];
  private failedAttempts = 0;
  private lockoutTimer: number | null = null;
  private copy = COPY.en;
  private readonly pinView: HTMLElement;
  private readonly panelView: HTMLElement;
  private readonly confirmView: HTMLElement;
  private readonly pinForm: HTMLFormElement;
  private readonly pinInput: HTMLInputElement;
  private readonly pinError: HTMLElement;
  private readonly unlockButton: HTMLButtonElement;

  constructor(private readonly options: ParentModeOptions) {
    this.pinView = this.required('.parent-pin-view');
    this.panelView = this.required('.parent-panel-view');
    this.confirmView = this.required('.parent-confirm-view');
    this.pinForm = this.required('.parent-pin-form');
    this.pinInput = this.required('.parent-pin-input');
    this.pinError = this.required('.parent-pin-error');
    this.unlockButton = this.required('.parent-unlock-button');

    options.trigger.addEventListener('click', () => this.recordTap());
    this.required<HTMLButtonElement>('.parent-mode-close').addEventListener('click', () => this.close());
    this.pinForm.addEventListener('submit', (event) => this.submitPin(event));
    this.required<HTMLButtonElement>('.parent-reset-button').addEventListener('click', () => this.showConfirmation());
    this.required<HTMLButtonElement>('.parent-cancel-button').addEventListener('click', () => this.showPanel());
    this.required<HTMLButtonElement>('.parent-confirm-button').addEventListener('click', () => this.resetBudget());
    options.modal.addEventListener('click', (event) => {
      if (event.target === options.modal) this.close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !options.modal.hidden) this.close();
    });
  }

  private recordTap(): void {
    const now = Date.now();
    this.tapTimes = this.tapTimes.filter((time) => now - time <= TAP_WINDOW_MS);
    this.tapTimes.push(now);
    if (this.tapTimes.length < REQUIRED_TAPS) return;
    this.tapTimes = [];
    this.open();
  }

  private open(): void {
    this.copy = COPY[this.options.getLocale()];
    this.applyCopy();
    this.relock();
    this.options.modal.hidden = false;
    window.setTimeout(() => this.pinInput.focus(), 0);
  }

  private close(): void {
    this.options.modal.hidden = true;
    this.relock();
  }

  private relock(): void {
    this.pinForm.reset();
    this.pinError.textContent = '';
    this.pinView.hidden = false;
    this.panelView.hidden = true;
    this.confirmView.hidden = true;
  }

  private submitPin(event: SubmitEvent): void {
    event.preventDefault();
    if (this.pinInput.value === PARENT_PIN) {
      this.failedAttempts = 0;
      this.showPanel();
      return;
    }

    this.failedAttempts += 1;
    this.pinInput.value = '';
    this.pinError.textContent = this.copy.incorrectPin;
    this.pinInput.focus();
    if (this.failedAttempts % 3 === 0) this.startLockout();
  }

  private startLockout(): void {
    const lockoutSeconds = Math.min(30, 5 * 2 ** (this.failedAttempts / 3 - 1));
    this.pinInput.disabled = true;
    this.unlockButton.disabled = true;
    this.pinError.textContent = this.copy.locked(lockoutSeconds);
    if (this.lockoutTimer !== null) window.clearTimeout(this.lockoutTimer);
    this.lockoutTimer = window.setTimeout(() => {
      this.lockoutTimer = null;
      this.pinInput.disabled = false;
      this.unlockButton.disabled = false;
      this.pinError.textContent = '';
      if (!this.options.modal.hidden) this.pinInput.focus();
    }, lockoutSeconds * 1_000);
  }

  private showPanel(): void {
    this.pinView.hidden = true;
    this.confirmView.hidden = true;
    this.panelView.hidden = false;
    this.required('.parent-time-left').textContent = formatTime(this.options.getTimeLeft());
    this.required('.parent-status').textContent = '';
  }

  private showConfirmation(): void {
    this.panelView.hidden = true;
    this.confirmView.hidden = false;
    this.required('.parent-reset-prompt').textContent = this.copy.resetPrompt(
      formatTime(this.options.getWatchedSeconds()),
    );
  }

  private resetBudget(): void {
    this.options.resetWatchBudget();
    this.showPanel();
    this.required('.parent-status').textContent = this.copy.resetComplete;
  }

  private applyCopy(): void {
    this.required('.parent-mode-title').textContent = this.copy.parentMode;
    this.required('.parent-pin-help').textContent = this.copy.pinHelp;
    this.required('.parent-pin-label').textContent = this.copy.pinLabel;
    this.unlockButton.textContent = this.copy.unlock;
    this.required('.parent-remaining-label').textContent = this.copy.remaining;
    this.required('.parent-reset-button').textContent = this.copy.reset;
    this.required('.parent-reset-title').textContent = this.copy.resetTitle;
    this.required('.parent-cancel-button').textContent = this.copy.cancel;
    this.required('.parent-confirm-button').textContent = this.copy.confirmReset;
    this.required<HTMLButtonElement>('.parent-mode-close').ariaLabel = this.copy.close;
  }

  private required<T extends Element = HTMLElement>(selector: string): T {
    const element = this.options.modal.querySelector<T>(selector);
    if (!element) throw new Error(`Missing parent mode element: ${selector}`);
    return element;
  }

}
